import { z } from 'zod';
import { mutationWithAuth, queryWithAuth } from '../graphql-helpers';
import { GET_DOG, GET_DOGS, ADD_DOG, EDIT_DOG, DELETE_DOG } from '../graphql/dogs';

export const DogSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    longName: z.string().optional(),
    sex: z.string().optional(),
    color: z.string().optional(),
    dateOfBirth: z.string().optional(),
    akc: z.string().optional(),
    category: z.string().optional(),
    ownedBy: z.string().optional(),
    dam: z.string().optional(),
    sire: z.string().optional(),
    hips: z.string().optional(),
    elbows: z.string().optional(),
    pra: z.string().optional(),
    eic: z.string().optional(),
    hpnk: z.string().optional(),
    coverImgUrl: z.string().optional(),
    imagesUrls: z.array(z.string()).optional(),
    imageUrl: z.string().optional(),
    content: z.string().optional(),
  })
  .passthrough();
export type Dog = z.infer<typeof DogSchema> & {
  id: string;
  dob?: string;
  longName?: string;
  category?: string;
  ownedBy?: string;
  dam?: string;
  sire?: string;
  hips?: string;
  elbows?: string;
  pra?: string;
  eic?: string;
  hpnk?: string;
  imagesUrls?: string[];
  content?: string;
  hipAndElbowUrl?: string;
  dnaUrl?: string;
  pedigreeUrl?: string;
  cardiacUrl?: string;
  eyeClearanceUrl?: string;
};

export const DogsSchema = z.array(DogSchema);

/**
 * List dogs with optional filtering
 */
export async function listDogs(
  params?: { category?: string; sex?: string },
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const filter = params
    ? {
        ...(params.category && {
          category: Array.isArray(params.category) ? params.category : [params.category],
        }),
        ...(params.sex && { sex: params.sex }),
      }
    : undefined;

  const response = await queryWithAuth<{ getDogs: Array<z.infer<typeof DogSchema>> }>(
    GET_DOGS,
    filter ? { filter } : undefined,
    getAccessTokenFn,
    false, // Public query, can use API key
  );

  const dogs = response.getDogs;
  return dogs.map((dog) => ({
    ...dog,
    dob: dog.dateOfBirth,
  })) as Dog[];
}

/**
 * Get a single dog by ID
 */
export async function getDog(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await queryWithAuth<{ getDog: z.infer<typeof DogSchema> }>(
    GET_DOG,
    { id },
    getAccessTokenFn,
    false,
  );

  const dog = response.getDog;
  return {
    ...dog,
    dob: dog.dateOfBirth,
  } as Dog;
}

/**
 * Create a new dog
 */
export async function createDog(
  input: Partial<Dog>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const dogInput = {
    name: input.name || '',
    longName: input.longName,
    ownedBy: input.ownedBy,
    dateOfBirth: input.dob || input.dateOfBirth || '',
    dam: input.dam,
    sire: input.sire,
    color: input.color,
    sex: input.sex || '',
    hips: input.hips,
    elbows: input.elbows,
    pra: input.pra,
    eic: input.eic,
    hpnk: input.hpnk,
    category: input.category || '',
    content: input.content,
    coverImgUrl: input.coverImgUrl || '',
    hipAndElbowUrl: input.hipAndElbowUrl,
    dnaUrl: input.dnaUrl,
    pedigreeUrl: input.pedigreeUrl,
    cardiacUrl: input.cardiacUrl,
    eyeClearanceUrl: input.eyeClearanceUrl,
    images: input.images,
    imagesUrls: input.imagesUrls || [],
  };

  const response = await mutationWithAuth<{ addDog: z.infer<typeof DogSchema> }>(
    ADD_DOG,
    { input: dogInput },
    getAccessTokenFn,
    true,
  );

  const dog = response.addDog;
  return {
    ...dog,
    dob: dog.dateOfBirth,
  } as Dog;
}

/**
 * Update an existing dog
 */
export async function updateDog(
  id: string,
  input: Partial<Dog>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const dogInput = {
    name: input.name || '',
    longName: input.longName,
    ownedBy: input.ownedBy,
    dateOfBirth: input.dob || input.dateOfBirth || '',
    dam: input.dam,
    sire: input.sire,
    color: input.color,
    sex: input.sex || '',
    hips: input.hips,
    elbows: input.elbows,
    pra: input.pra,
    eic: input.eic,
    hpnk: input.hpnk,
    category: input.category || '',
    content: input.content,
    coverImgUrl: input.coverImgUrl || '',
    hipAndElbowUrl: input.hipAndElbowUrl,
    dnaUrl: input.dnaUrl,
    pedigreeUrl: input.pedigreeUrl,
    cardiacUrl: input.cardiacUrl,
    eyeClearanceUrl: input.eyeClearanceUrl,
    images: input.images,
    imagesUrls: input.imagesUrls || [],
  };

  const response = await mutationWithAuth<{ editDog: z.infer<typeof DogSchema> }>(
    EDIT_DOG,
    { id, input: dogInput },
    getAccessTokenFn,
    true,
  );

  const dog = response.editDog;
  return {
    ...dog,
    dob: dog.dateOfBirth,
  } as Dog;
}

/**
 * Delete a dog
 */
export async function deleteDog(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await mutationWithAuth<{ deleteDog: boolean }>(
    DELETE_DOG,
    { id },
    getAccessTokenFn,
    true,
  );

  return { success: response.deleteDog };
}
